import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useRoute } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Trophy, 
  Users, 
  Calendar, 
  Clock, 
  ChevronDown,
  Award,
  Brackets
} from 'lucide-react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AdminTournamentPage() {
  const { user } = useAuth();
  const [, params] = useRoute('/admin/tournament/:id');
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const tournamentId = params?.id ? parseInt(params.id) : -1;
  
  // Redirect if not admin
  if (!user || user.role !== 'admin') {
    setLocation('/');
    return null;
  }
  
  const { data: tournament, isLoading, refetch } = useQuery({
    queryKey: ['admin-tournament', tournamentId],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/admin/tournaments/${tournamentId}`);
      if (!res.ok) throw new Error('Failed to fetch tournament');
      return await res.json();
    },
    enabled: tournamentId > 0,
  });
  
  const { data: matches, isLoading: isLoadingMatches } = useQuery({
    queryKey: ['admin-tournament-matches', tournamentId],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/admin/tournaments/${tournamentId}/matches`);
      if (!res.ok) throw new Error('Failed to fetch tournament matches');
      return await res.json();
    },
    enabled: tournamentId > 0,
  });
  
  // Mutation for updating tournament status
  const updateTournamentMutation = useMutation({
    mutationFn: async (status: string) => {
      const res = await apiRequest(
        'PATCH', 
        `/api/admin/tournaments/${tournamentId}`, 
        { status }
      );
      return await res.json();
    },
    onSuccess: () => {
      toast({
        title: 'Tournament updated',
        description: 'Tournament status has been successfully updated',
      });
      refetch();
      queryClient.invalidateQueries({ queryKey: ['admin-tournament', tournamentId] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error updating tournament',
        description: error.message || 'An error occurred while updating the tournament',
        variant: 'destructive',
      });
    },
  });
  
  const handleBackToAdmin = () => {
    setLocation('/admin');
  };
  
  const handleUpdateStatus = (status: string) => {
    updateTournamentMutation.mutate(status);
  };
  
  if (isLoading) {
    return (
      <div className="container mx-auto p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading tournament details...</p>
        </div>
      </div>
    );
  }
  
  if (!tournament) {
    return (
      <div className="container mx-auto p-8">
        <div className="mb-6">
          <Button variant="outline" size="sm" onClick={handleBackToAdmin}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Admin
          </Button>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Tournament Not Found</CardTitle>
            <CardDescription>
              The requested tournament could not be found or you don't have permission to view it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleBackToAdmin}>Return to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8 pb-20">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={handleBackToAdmin}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Admin
          </Button>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Trophy className="h-8 w-8" />
            Tournament Details
          </h1>
        </div>
        
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Status <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Change Status</DropdownMenuLabel>
              <DropdownMenuItem 
                onClick={() => handleUpdateStatus("pending")}
                disabled={tournament.status === "pending"}
              >
                Set to Pending
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleUpdateStatus("active")}
                disabled={tournament.status === "active" || tournament.status === "completed"}
              >
                Set to Active
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleUpdateStatus("completed")}
                disabled={tournament.status === "completed"}
              >
                Set to Completed
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleUpdateStatus("cancelled")}
                disabled={tournament.status === "cancelled"}
              >
                Set to Cancelled
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Tournament Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge className={
              tournament.status === "completed" ? "bg-green-100 text-green-800" : 
              tournament.status === "active" ? "bg-blue-100 text-blue-800" :
              tournament.status === "cancelled" ? "bg-red-100 text-red-800" : 
              "bg-yellow-100 text-yellow-800"
            }>
              {tournament.status}
            </Badge>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Participants</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Array.isArray(tournament.participants) ? tournament.participants.length : 0}/{tournament.maxPlayers}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Current Round</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tournament.currentRound}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Winner</CardTitle>
          </CardHeader>
          <CardContent>
            {tournament.winnerUserId ? (
              <div className="flex items-center">
                <Award className="h-5 w-5 mr-2 text-yellow-500" />
                <span>User ID {tournament.winnerUserId}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">No winner yet</span>
            )}
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Tournament Details</TabsTrigger>
          <TabsTrigger value="matches">Matches</TabsTrigger>
          <TabsTrigger value="participants">Participants</TabsTrigger>
        </TabsList>
        
        <TabsContent value="details" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Tournament Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Basic Information</h3>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium">ID:</span> {tournament.id}
                    </div>
                    <div>
                      <span className="font-medium">Name:</span> {tournament.name}
                    </div>
                    <div>
                      <span className="font-medium">Description:</span> {tournament.description || 'No description'}
                    </div>
                    <div>
                      <span className="font-medium">Created by:</span> User ID {tournament.creator}
                    </div>
                    <div>
                      <span className="font-medium">Created at:</span> {new Date(tournament.createdAt).toLocaleString()}
                    </div>
                    <div>
                      <span className="font-medium">Updated at:</span> {new Date(tournament.updatedAt).toLocaleString()}
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Tournament Dates</h3>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="font-medium">Start Date:</span>{' '}
                      {tournament.startDate ? new Date(tournament.startDate).toLocaleString() : 'Not started'}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="font-medium">End Date:</span>{' '}
                      {tournament.endDate ? new Date(tournament.endDate).toLocaleString() : 'Not ended'}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Bracket Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <span className="font-medium">Bracket Type:</span> Single Elimination
                </div>
                <div>
                  <span className="font-medium">Current Round:</span> {tournament.currentRound}
                </div>
                <pre className="p-4 bg-muted/50 rounded-md overflow-x-auto mt-4 text-xs">
                  {JSON.stringify(tournament.bracketData, null, 2)}
                </pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="matches" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Tournament Matches</CardTitle>
              <CardDescription>
                {isLoadingMatches ? 'Loading matches...' : 
                  matches && matches.length > 0 ? `${matches.length} matches found` : 'No matches found'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingMatches ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
                </div>
              ) : !matches || matches.length === 0 ? (
                <div className="text-center py-8 border rounded-lg bg-muted/50">
                  No matches found for this tournament
                </div>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">ID</th>
                        <th className="text-left p-2">Round</th>
                        <th className="text-left p-2">Match #</th>
                        <th className="text-left p-2">Player 1</th>
                        <th className="text-left p-2">Player 2</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Winner</th>
                        <th className="text-left p-2">Game Room</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matches.map((match: any) => (
                        <tr key={match.id} className="border-t hover:bg-muted/50">
                          <td className="p-2">{match.id}</td>
                          <td className="p-2">{match.round}</td>
                          <td className="p-2">{match.matchNumber}</td>
                          <td className="p-2">{match.player1Id}</td>
                          <td className="p-2">{match.player2Id}</td>
                          <td className="p-2">
                            <Badge className={
                              match.status === "completed" ? "bg-green-100 text-green-800" : 
                              match.status === "active" ? "bg-blue-100 text-blue-800" : 
                              "bg-yellow-100 text-yellow-800"
                            }>
                              {match.status}
                            </Badge>
                          </td>
                          <td className="p-2">{match.winnerId || 'No winner'}</td>
                          <td className="p-2">
                            {match.gameRoomId ? (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setLocation(`/admin/game-room/${match.gameRoomId}`)}
                              >
                                View Game
                              </Button>
                            ) : (
                              'No game'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="participants" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Tournament Participants</CardTitle>
              <CardDescription>
                {Array.isArray(tournament.participants) ? tournament.participants.length : 0} participants 
                out of {tournament.maxPlayers} maximum
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!tournament.participants || tournament.participants.length === 0 ? (
                <div className="text-center py-8 border rounded-lg bg-muted/50">
                  No participants in this tournament
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {tournament.participants.map((userId: number, index: number) => (
                    <Card key={index} className="overflow-hidden">
                      <CardContent className="p-4 flex items-center">
                        <div className="bg-muted rounded-full h-10 w-10 flex items-center justify-center mr-3">
                          <Users className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="font-medium">User ID: {userId}</div>
                          <div className="text-sm text-muted-foreground">
                            Seed #{index + 1}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}